import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useRef, forwardRef, useImperativeHandle } from "react";

const leafletImage = require("leaflet-image");

type LatLng = {
  lat: number;
  lng: number;
};

type Mode = "create" | "edit" | "preview";

export type LocationPickerRef = {
  generateStaticImage: () => Promise<File | null>;
};

type Props = {
  value?: LatLng | null;
  onChange?: (latlng: LatLng) => void;
  mode?: Mode;
  height?: string;
  zoom?: number;
};

const markerIcon = new L.Icon({
  iconUrl: "/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

function ClickToPick({
  disabled,
  onPick,
}: {
  disabled: boolean;
  onPick?: (latlng: LatLng) => void;
}) {
  useMapEvents({
    click(e) {
      if (!disabled && onPick) {
        onPick(e.latlng);
      }
    },
  });
  return null;
}

const LocationPickerMap = forwardRef<LocationPickerRef, Props>(
  (
    {
      value = null,
      onChange,
      mode = "create",
      height = 'auto',
      zoom = 14,
    },
    ref
  ) => {
    const mapRef = useRef<L.Map | null>(null);
    const isReadOnly = mode === "preview";

    const generateStaticImage = (): Promise<File | null> => {
      return new Promise((resolve) => {
        if (!mapRef.current) return resolve(null);

        leafletImage(
          mapRef.current,
          (err: any, canvas: HTMLCanvasElement) => {
            if (err || !canvas) return resolve(null);

            canvas.toBlob((blob) => {
              if (!blob) return resolve(null);

              resolve(
                new File([blob], "location-map.png", {
                  type: "image/png",
                })
              );
            });
          }
        );
      });
    };

    useImperativeHandle(ref, () => ({
      generateStaticImage,
    }));

    return (
      <MapContainer
        center={
          value ? [value.lat, value.lng] : [23.8103, 90.4125]
        }
        zoom={zoom}
        className={height == 'auto' ? "square-shape" : ""}
        style={{ width: "100%", height: height }}
        ref={mapRef}
        dragging={!isReadOnly}
        scrollWheelZoom={!isReadOnly}
        zoomControl={!isReadOnly}
      >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                // attribution="&copy; OpenStreetMap contributors"
                crossOrigin="anonymous"
                />

        <ClickToPick disabled={isReadOnly} onPick={onChange} />

        {value && (
          <Marker
            position={value}
            icon={markerIcon}
            draggable={false}
          />
        )}
      </MapContainer>
    );
  }
);

export default LocationPickerMap;
